"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validatePermissionPathComply = exports.validatePermissionsPaths = exports.getHierarchyPaths = exports.validatePermissions = exports.permissionVerifier = exports.permissionMapper = void 0;
const lodash_1 = require("lodash");
const definitions_1 = require("./definitions");
/**
 * Used to map the permissions from the groups reponse.
 *
 * @param groupPermissions The group's permissions assigned
 * @return Returns the group's permissions with their module and label.
 */
function permissionMapper(groupPermissions) {
    // Verifies if all the permissions are valid.
    validatePermissions(groupPermissions);
    // Maps all the available permissions to make it more accessible.
    const map = new Map(Object.entries(definitions_1.permissions));
    // Process and maps the response permissions.
    return groupPermissions.reduce((maped, permission) => {
        // Checks in what module the permission belongs
        map.forEach((value, module) => {
            // Gets and verifies if the permission belongs to the module.
            const permissionValue = lodash_1.get(value, permission);
            if (permissionValue) {
                // Sets the permission inside the right module with its label.
                lodash_1.set(maped, `${module}.${permission}`, permissionValue);
            }
        });
        return maped;
    }, {});
}
exports.permissionMapper = permissionMapper;
/**
 * Verifies if the user's permissions satisfy the wanted action or resource access.
 *
 * @param groups The user's groups assigned.
 * @param permission The required permission or list of permissions
 * @param strict True to require all the required permissions. False to apply some or just one required permission.
 * This parameter is only applicable with collections of permissions.
 * @return Returns true if the user satisfies the required permission.
 */
function permissionVerifier(groups, requiredPermission, strict = true) {
    // Merges all the groups' permissions in one single array.
    const groupsPermissions = groups.reduce((list, group) => {
        return lodash_1.union(list, group.permissions);
    }, []);
    // Verifies all the permissions are valid.
    validatePermissions(groupsPermissions);
    let satisfyPermission = false;
    // Process if the required permission is an array.
    if (Array.isArray(requiredPermission)) {
        if (strict) {
            // Verify if satisfies all the required permissions.
            satisfyPermission =
                requiredPermission.filter((item) => groupsPermissions.includes(item)).length === requiredPermission.length;
        }
        else {
            // Verify if satisfies as minimum one required permission.
            satisfyPermission = requiredPermission.some((item) => groupsPermissions.includes(item));
        }
    }
    else {
        // Verify if the user has been assigned the required permission.
        satisfyPermission = groupsPermissions.includes(requiredPermission);
    }
    if (satisfyPermission) {
        return true;
    }
    // The user doesn't has assigned any required permission.
    return false;
}
exports.permissionVerifier = permissionVerifier;
/**
 * Validates if all the permissions on a list are valid.
 *
 * @param list The list of permissions to verify.
 * @returns Returns True if all the permissions exist, throws an error if one permission not exists.
 */
function validatePermissions(list) {
    // Extracts all the permissions available.
    const allPermissions = Object.values(definitions_1.permissions).reduce((permissions, module) => {
        const modulePermissions = Object.keys(module);
        return lodash_1.concat(permissions, modulePermissions);
    }, []);
    // Verifies if the list has any invalid permission.
    for (const permission of list) {
        if (!allPermissions.includes(permission)) {
            // Throws an error after finding an invalid permission.
            throw Error(`Permission not found: "${permission}". Please check the list of available permissions`);
        }
    }
    // All passed.
    return true;
}
exports.validatePermissions = validatePermissions;
/**
 * Get all possible permission paths of a child permission
 *
 * @param Permission Target permission
 * @returns Returns list of paths (paths are also lists)
 */
function getHierarchyPaths(permission, paths) {
    const parentPermissions = definitions_1.permissionDependentMap.get(permission);
    // Means permission is a parent permission
    if (parentPermissions && (parentPermissions === null || parentPermissions === void 0 ? void 0 : parentPermissions.length) === 0) {
        paths.push([permission]);
        return;
    }
    parentPermissions === null || parentPermissions === void 0 ? void 0 : parentPermissions.forEach((parent) => {
        getHierarchyPaths(parent, paths);
        paths[paths.length - 1].push(permission);
    });
    return paths;
}
exports.getHierarchyPaths = getHierarchyPaths;
/**
 * Validates every element(and it's dependencies) of permissionsToVerify are contained
 * in the assignedPermissions list
 *
 * @param Permission Target permissions to verify.
 * @param Permission[] The assigned permissions list
 * @returns Returns True if all necessary permissions are present.
 */
function validatePermissionsPaths(permissionsToVerify, assignedPermissions) {
    if (!assignedPermissions) {
        assignedPermissions = permissionsToVerify;
    }
    // Validates for each element in permissionsToVerify at least one path comply.
    return permissionsToVerify.every((targetPermission) => validatePermissionPathComply(targetPermission, assignedPermissions));
}
exports.validatePermissionsPaths = validatePermissionsPaths;
/**
 * Validates if assignedPermissions contains at least one permissions path of the target permission.
 * Permission can be child of multiple permissions which can also be dependent on other permissions
 * if a permission has more than one parent permission, the rule is: assigned permissions should
 * contain not only the target permission but also at least one of the parent permissions (This should
 * happen recursively as there are multiple leves of dependency)
 * Then we can describe "path" as a tree where all permissions in there are required in order to be able
 * to activate/assign the target permission.
 *
 * @param Permission Permissions to verify.
 * @param Permission[] The list of permissions assined to the user.
 * @returns Returns True if user is assigned to the parent(s) permission
 */
function validatePermissionPathComply(permission, assignedPermissions) {
    var _a;
    const parentPermissions = definitions_1.permissionDependentMap.get(permission);
    // Validate only for no root permissions
    if (parentPermissions && parentPermissions.length > 0) {
        const permissionPaths = (_a = getHierarchyPaths(permission, [])) !== null && _a !== void 0 ? _a : [];
        // At least one path must comply
        const isAtLeastOnePathPresent = permissionPaths.some((path) => {
            return path.every((item) => assignedPermissions === null || assignedPermissions === void 0 ? void 0 : assignedPermissions.includes(item));
        });
        if (!isAtLeastOnePathPresent) {
            throw Error(`Permission "${permission}" must comply at least one of the following permission paths: "${permissionPaths.join(' OR ')}". Please assign one of them to continue`);
        }
    }
    return true;
}
exports.validatePermissionPathComply = validatePermissionPathComply;
//# sourceMappingURL=utils.js.map